import React, { useEffect, useState } from "react";
import { useTheme } from "styled-components";
import { Modal, Drawer, Toast } from "@wrstudios/components";
import { IconPencil } from "@wrstudios/icons";
import Image from "../../common/Image";
import Loading from "../../common/Loading";
import SubjectPropertyPin from "../../common/SubjectPropertyPin";
import {
  ComparableImage,
  ComparableAddress,
  ComparableDifferencesDynamic
} from "../../comparable";
import { useLive } from "../LiveProvider";
import IllustrationScale from "../../illustrations/IllustrationScale";
import {
  Comparable,
  Overflow,
  Header,
  HeaderEdit,
  GlobalStyle
} from "./styled/live-listing";
import {
  Guide,
  GuideIllustration,
  GuideTitle,
  Color,
  List,
  Item,
  MapStatic,
  ModalContent,
  Actions,
  Action,
  Button,
  Cancel
} from "./styled/live-listing-subject-property";
import LiveListingSubjectPropertyDrawer from "./LiveListingSubjectPropertyDrawer";
import { ErrorBoundary } from "react-error-boundary";

function LiveListingSubjectProperty(props) {
  const theme = useTheme();
  const {
    subjectProperty,
    isReportOwnedByCurrentAgent,

    comparisonFields,
    setComparisonFields,
    comparisonFieldsDefault
  } = useLive();

  const [showDrawer, setShowDrawer] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [updateFromModal, setUpdateFromModal] = useState(false);
  const [activeUpdate, setActiveUpdate] = useState(false);

  const [loading, setLoading] = useState(false);
  const [toasterMessage, setToasterMessage] = useState({ status: "", msg: "" });

  const [comparisonFieldsWithValues, setComparisonFieldsWithValues] = useState(
    []
  );
  const [activeFields, setActiveFields] = useState([]);
  const [inactiveFields, setInactiveFields] = useState([]);
  const capitalize = (s) => {
    if (typeof s !== "string") return "";
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  useEffect(() => {
    setComparisonFieldsWithValues(
      comparisonFields.map((obj) => {
        if (subjectProperty[obj.lookupName] !== undefined) {
          obj.value = subjectProperty[obj.lookupName];
          if (
            obj.lookupName === "lotSize" &&
            subjectProperty.landMeasurement !== ""
          ) {
            obj.additionalDisplayText = ` (${capitalize(
              subjectProperty.landMeasurement
            )})`;
          }
          if (obj.lookupName === "sqft" && subjectProperty.sqftType !== "") {
            obj.additionalDisplayText = ` (${capitalize(
              subjectProperty.sqftType
            )})`;
          }
        }
        return obj;
      })
    );
  }, [subjectProperty, comparisonFields]);

  useEffect(() => {
    setActiveFields(comparisonFieldsWithValues.filter((obj) => obj.isActive));

    setInactiveFields(
      comparisonFieldsWithValues.filter((obj) => !obj.isActive)
    );
  }, [comparisonFieldsWithValues]);

  useEffect(() => {
    setInactiveFields(
      inactiveFields.sort(function (a, b) {
        var dataA = a.displayName;
        var dataB = b.displayName;
        return dataA < dataB ? -1 : dataA > dataB ? 1 : 0;
      })
    );
  }, [inactiveFields]);

  useEffect(() => {
    const attachToDrawer = () => {
      window.scrollTo(
        window.scrollX,
        document.querySelector("#middle-scrollable-section").scrollTop
      );
    };

    const attachToWindow = () =>
      document.querySelector("#middle-scrollable-section")
        ? (document.querySelector("#middle-scrollable-section").scrollTop =
            window.scrollY)
        : null;

    document.querySelector("#middle-scrollable-section")
      ? document
          .querySelector("#middle-scrollable-section")
          .addEventListener("scroll", attachToDrawer)
      : null;

    window.addEventListener("scroll", attachToWindow);

    !showDrawer ? window.removeEventListener("scroll", attachToWindow) : null;

    return () => {
      window.removeEventListener("scroll", attachToWindow);
      document.querySelector("#middle-scrollable-section")
        ? document
            .querySelector("#middle-scrollable-section")
            .removeEventListener("scroll", attachToDrawer)
        : null;
    };
  });

  const SaveAndExit = () => {
    setUpdateFromModal(true);
    setShowCloseModal(false);
  };

  const ExitWithoutSaving = () => {
    setComparisonFields(
      comparisonFields.map((obj) => {
        if (obj.isDraft === true) {
          obj.isActive = false;
          obj.isDraft = false;
          obj["isTemp"] = false;
        }
        return obj;
      })
    );

    setComparisonFields(comparisonFieldsDefault);
    setActiveUpdate(false);
    setShowDrawer(false);
    setShowCloseModal(false);
  };
  const ErrorComponent = () => {
    window.location.href = "/500";
  };
  return (
    <>
      <ErrorBoundary FallbackComponent={ErrorComponent}>
        <GlobalStyle />
        <Comparable {...props} isSubjectProperty>
          {toasterMessage.status && (
            <Toast
              variant={toasterMessage.status}
              message={toasterMessage.msg}
            />
          )}

          <ComparableImage>
            {!subjectProperty.photo &&
              !!subjectProperty.coords.lat &&
              !!subjectProperty.coords.lon && (
                <MapStatic
                  width={328}
                  height={210}
                  lat={subjectProperty.coords.lat}
                  lon={subjectProperty.coords.lon}
                  pin={<SubjectPropertyPin color={theme.live.colors.primary} />}
                />
              )}
            {!!subjectProperty.photo && (
              <Image
                src={subjectProperty.photo}
                data-error="Image Unavailable"
                alt="Image Unavailable"
              />
            )}
          </ComparableImage>
          <ComparableAddress
            street={subjectProperty.street}
            cityStateZip={subjectProperty.cityStateZip}
            status="Subject"
            statusColor={theme.styleGuide.colors.purple[500]}
            price={0}
            isSubjectProperty
          />
          <ComparableDifferencesDynamic subProp={comparisonFieldsWithValues} />
          <Overflow>
            {!isReportOwnedByCurrentAgent && (
              <Header
                title="Your Comp Guide"
                className="comparable-group-header">
                Your Comp Guide
              </Header>
            )}
            {isReportOwnedByCurrentAgent && (
              <HeaderEdit
                title="Edit Property Details"
                onClick={() => setShowDrawer(true)}>
                <IconPencil />
                Edit Property Details
              </HeaderEdit>
            )}
            <Guide>
              <GuideIllustration>
                <IllustrationScale />
              </GuideIllustration>
              <GuideTitle>Listing Status</GuideTitle>
              <List>
                <Item>
                  <Color status="active" />
                  <span>
                    <strong>Active</strong> <i>(For Sale)</i> are current
                    competition
                  </span>
                </Item>
                <Item>
                  <Color status="sold" />
                  <span>
                    <strong>Sold</strong> determine recent market value
                  </span>
                </Item>
                <Item>
                  <Color status="pending" />
                  <span>
                    <strong>Pending</strong> signal where the market is heading
                  </span>
                </Item>
                <Item>
                  <Color status="withdrawn" />
                  <span>
                    <strong>Withdrawn/Expired</strong> indicate priced too high
                  </span>
                </Item>
              </List>
            </Guide>
          </Overflow>
        </Comparable>
        {showDrawer && (
          <>
            <Drawer
              onClose={() => {
                activeUpdate ? setShowCloseModal(true) : setShowDrawer(false);
              }}>
              <LiveListingSubjectPropertyDrawer
                comparisonFields={comparisonFields}
                setComparisonFields={setComparisonFields}
                setActiveFields={setActiveFields}
                setInactiveFields={setInactiveFields}
                setShowEditDrawer={setShowDrawer}
                activeFields={activeFields}
                inactiveFields={inactiveFields}
                activeUpdate={activeUpdate}
                setActiveUpdate={setActiveUpdate}
                setLoading={setLoading}
                setToasterMessage={setToasterMessage}
                updateFromModal={updateFromModal}
                setUpdateFromModal={setUpdateFromModal}
              />
            </Drawer>
          </>
        )}

        {!loading ? (
          showCloseModal && (
            <Modal onClose={() => setShowCloseModal(false)}>
              <ModalContent>
                <Modal.Header>
                  Unsaved Changes
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body>
                  You've made some changes on this screen. Would you like to
                  save these changes or exit without saving?
                </Modal.Body>
                <Modal.Footer>
                  <Actions>
                    <Action>
                      <Cancel type="button" onClick={ExitWithoutSaving}>
                        Exit Without Saving
                      </Cancel>
                    </Action>

                    <Action>
                      <Button disabled={loading} onClick={SaveAndExit}>
                        {loading ? (
                          <Loading>Save and Exit</Loading>
                        ) : (
                          "Save and Exit"
                        )}
                      </Button>
                    </Action>
                  </Actions>
                </Modal.Footer>
              </ModalContent>
            </Modal>
          )
        ) : (
          <Modal>
            <ModalContent>
              <Modal.Body>
                <Loading>Saving</Loading>
              </Modal.Body>
            </ModalContent>
          </Modal>
        )}
      </ErrorBoundary>
    </>
  );
}

export default LiveListingSubjectProperty;
